import moment from 'moment';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { NotificationStatus } from '../../types/notification-status';

export class StatusModel {
    protected clientManager: ClientManagerInterface;
    public constructor(
        public statusCode: NOTIFICATION_STATUS_CODE,
        public startTime: moment.Moment,
        public endTime: moment.Moment,
    ) {
        const injector = CustomInjector.getInjector();
        this.clientManager = injector.get(ClientManagerInstanceToken);
        this.startTime = this.startTime.startOf('minute');
        this.endTime = this.endTime.startOf('minute');
    }

    public toString(): string {
        return (
            'Status: ' +
            this.statusCode +
            ' | StartTime: ' +
            this.startTime.format('YYYY-MM-DD HH:mm:ss') +
            ' | EndTime: ' +
            this.endTime.format('YYYY-MM-DD HH:mm:ss')
        );
    }

    // ever y status except DISP can be escalated
    public canEscalate(): boolean {
        if (this.clientManager.isPaceUS()) {
            return false;
        }
        return this.statusCode !== NOTIFICATION_STATUS_CODE.DISP;
    }

    public isInProgress(): boolean {
        return (
            this.statusCode === NOTIFICATION_STATUS_CODE.OSR ||
            this.statusCode === NOTIFICATION_STATUS_CODE.TRAV ||
            this.statusCode === NOTIFICATION_STATUS_CODE.ROSR ||
            this.statusCode === NOTIFICATION_STATUS_CODE.FCC
        );
    }

    public canChangeCriticality(): boolean {
        return (
            this.statusCode === NOTIFICATION_STATUS_CODE.ETA ||
            this.statusCode === NOTIFICATION_STATUS_CODE.TRAV ||
            this.statusCode === NOTIFICATION_STATUS_CODE.OSR
        );
    }

    public isOnSiteWork(): boolean {
        return this.statusCode === NOTIFICATION_STATUS_CODE.OSR || this.statusCode === NOTIFICATION_STATUS_CODE.TRAV;
    }

    public isRemoteWork(): boolean {
        return this.isRemoteSupport() || this.isRemoteWorkOrTraining();
    }

    /**
     * If status code is ROSR i.e. Remote work/Training or
     * If Status code is RTOBS i.e. when user selects Remote work and do sign later, then RTOBS status is saved
     * ROSR and RTOBS is considered as Remote work/training
     */
    public isRemoteWorkOrTraining(): boolean {
        return this.statusCode === NOTIFICATION_STATUS_CODE.ROSR || this.statusCode === NOTIFICATION_STATUS_CODE.RTOBS;
    }

    /**
     * If status code is FCC i.e. Remote Support or
     * If Status code is FTOBS i.e. when user selects Remote support and do sign later, then FTOBS status is saved
     * FCC and FTOBS is considered as Remote work/training
     */
    public isRemoteSupport(): boolean {
        return this.statusCode === NOTIFICATION_STATUS_CODE.FCC || this.statusCode === NOTIFICATION_STATUS_CODE.FTOBS;
    }

    public isToBeSigned(): boolean {
        return (
            this.statusCode === NOTIFICATION_STATUS_CODE.TOBS ||
            this.statusCode === NOTIFICATION_STATUS_CODE.RTOBS ||
            this.statusCode === NOTIFICATION_STATUS_CODE.FTOBS
        );
    }

    public isCancelled(): boolean {
        return (
            this.statusCode === NOTIFICATION_STATUS_CODE.AVR1 ||
            this.statusCode === NOTIFICATION_STATUS_CODE.AVR2 ||
            this.statusCode === NOTIFICATION_STATUS_CODE.CANC
        );
    }

    public isConfirmedOrCancellationConfirmed(): boolean {
        return this.statusCode === NOTIFICATION_STATUS_CODE.CRM || this.statusCode === NOTIFICATION_STATUS_CODE.CNCL;
    }

    public getStatusState(): NotificationStatus {
        return {
            statusCode: this.statusCode,
            startTime: this.startTime.toISOString(),
            endTime: this.endTime.toISOString(),
        };
    }

    public isOsrOrToBeSigned(): boolean {
        return (
            this.statusCode === NOTIFICATION_STATUS_CODE.OSR ||
            this.statusCode === NOTIFICATION_STATUS_CODE.SOSR ||
            this.isToBeSigned()
        );
    }
}

export class StatusModelFromJson extends StatusModel {
    public constructor(data: JsonStatusModel) {
        super(data.statusCode, moment(data.startTime), moment(data.endTime));
    }
}

// Defi nes the sequence of the different notifications states for sorting the status array

// TODO remove the cancellation task types after finding permanent fix for ticket https://evorait.atlassian.net/browse/SHCNGMAAP2-1809
export enum STATUS_ORDER {
    'DISP',
    'ETA',
    'REMT',
    'ROSR',
    'FCC',
    'TRAV',
    'ATS',
    'OSR',
    'SOSR',
    'TOBS',
    'RTOBS',
    'FTOBS',
    'AVR1',
    'AVR2',
    'AVRF',
    'CANC',
    'CNCL',
    'CRM',
}

export interface JsonStatusModel {
    statusCode: NOTIFICATION_STATUS_CODE;
    startTime: string;
    endTime: string;
}
